import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { StandardForm } from '@vp/swan';
import { bugtracker } from '@99designs/design-services-common';
import { usePage } from '@99designs/tracking';
import { useBriefContext } from '../BriefContext';
import { BriefFormProvider } from '../BriefContext/BriefFormContext';
import { MultiStep } from '../MultiStepSwanWizard/MultiStep';
import { Brief, FormInput } from './BriefForm';
import { BriefSkeleton } from './BriefSkeleton';
import { FieldGroup, fieldGroups, useFormGroups } from './FieldGroup';
import { FieldProcessingProvider } from './FieldProcessingProvider';
import { Review } from './Review';
import { ViewContextProvider, useIsEditMode } from './ViewContext';
import { useGetSubmitMultiStep } from './getSubmit';
import { useRegisterWithTracking } from './useRegisterWithTracking';

function useIsStepValid(
    groups: ReturnType<typeof fieldGroups>,
    trigger: (name?: string | string[]) => Promise<boolean>
) {
    return useCallback(
        async (currentStep: number) => {
            // We subtract 1 from the current step because the intro step is not a form group

            const group = groups[currentStep - 1];
            if (!group) {
                return true;
            }

            return await trigger(group.fields.map((f) => f.id));
        },
        [groups, trigger]
    );
}

export const ReviewForm: React.FC<{ brief: Brief }> = ({ brief }) => {
    const isEditMode = useIsEditMode();
    const {
        intro,
        groups,
        workEntityField,
        isLoading: isCriteriaLoading,
    } = useFormGroups(brief, undefined, true, isEditMode);

    const { pageData } = usePage();
    const {
        product,
        logomakerAsset,
        reportedIssues,
        config: {
            titleCopy,
            tenant: { title, exit },
        },
    } = useBriefContext();
    const getSubmitMultiStep = useGetSubmitMultiStep();

    const methods = useForm<FormInput>({
        shouldUnregister: false,
    });

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        clearErrors,
        setError,
        formState: { errors: formErrors, dirtyFields },
        getValues,
        trigger,
    } = methods;

    useEffect(() => {
        if (logomakerAsset && workEntityField) {
            bugtracker.notify(
                new Error(
                    'Both logomakerAsset and workEntityField are present in ReviewForm which is unexpected.'
                )
            );
        }
    }, [logomakerAsset, workEntityField]);

    const registerWithTracking = useRegisterWithTracking(
        brief,
        register,
        getValues,
        dirtyFields,
        pageData
    );

    const [groupFormKey, setGroupFormKey] = useState(0);
    const invitedDesigner = brief.request?.invitedDesigners?.[0];

    const allGroups = [intro, ...groups];
    const validate = useIsStepValid(allGroups, trigger);

    const steps = [
        <ViewContextProvider value={{ isReviewMode: true }}>
            <Review
                groups={allGroups}
                briefId={brief.id}
                groupFormSections={allGroups.reduce((acc, g) => {
                    acc[g.id] = (
                        <FieldGroup
                            key={`${g.id}-${groupFormKey}`}
                            group={g}
                            register={registerWithTracking}
                            errors={formErrors}
                            setValue={setValue}
                            watch={watch}
                            clearErrors={clearErrors}
                            setError={setError}
                        />
                    );
                    return acc;
                }, {} as Record<string, React.ReactNode>)}
                product={product || null}
                startingAsset={logomakerAsset || workEntityField}
                reportedIssues={reportedIssues}
                groupFormKey={groupFormKey}
                setGroupFormKey={setGroupFormKey}
                invitedDesigner={invitedDesigner}
            />
        </ViewContextProvider>,
    ];

    if (isCriteriaLoading) {
        return <BriefSkeleton />;
    }

    if (brief.groups === undefined) {
        return null;
    }

    return (
        <StandardForm
            style={{ height: '100%' }}
            onSubmit={getSubmitMultiStep(handleSubmit, brief.id)}
            /**
             * By default, browsers will submit a form when the Enter key is pressed while focused on input fields,
             * which is problematic as it can lead to unintentional submissions.
             * This prop will prevent form submission on Enter key press for input fields (other than file) and select
             * fields
             */
            onKeyDown={(e: React.KeyboardEvent<HTMLFormElement>) => {
                if (
                    e.key === 'Enter' &&
                    ((e.target instanceof HTMLInputElement && e.target.type !== 'file') ||
                        e.target instanceof HTMLSelectElement)
                )
                    e.preventDefault();
            }}
        >
            <FieldProcessingProvider>
                <BriefFormProvider briefId={brief.id}>
                    <FormProvider {...methods}>
                        <MultiStep
                            steps={steps}
                            validate={validate}
                            hideExitPopUp={isEditMode}
                            title={title(titleCopy, brief.request, brief.product)}
                            exitUrl={exit(product?.fulfilmentMpvId || '')}
                        />
                    </FormProvider>
                </BriefFormProvider>
            </FieldProcessingProvider>
        </StandardForm>
    );
};
